/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function Reference({ version = '' }) {
  return (
    <>
      {version ? (
        <Meta
          title={`Vimeo API ${version} Reference`}
          description={`Find comprehensive information about every endpoint in the Vimeo API ${version}, including available HTTP methods, supported parameters and values, and plain-language descriptions of HTTP status codes.`}
        />
      ) : (
        <Meta
          title="Vimeo API Reference"
          description="Find comprehensive information about every endpoint in the Vimeo API, including available HTTP methods, supported parameters and values, and plain-language descriptions of HTTP status codes."
        />
      )}
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/apps'}>API</Path.Link>
            <Path.Current>API Reference</Path.Current>
          </Path>
        }
        nav={{ page: 'api-reference' }}
      >
        <Header>Using the API Reference</Header>

        <Paragraph size="2">
          <em>Congratulations!</em> You've hit the motherlode. This is the Vimeo API reference, your
          comprehensive guide to information about all the methods, endpoints, fields, and values
          that go into the Vimeo API. If it's listed on these reference pages, it's available for
          your development project.
        </Paragraph>

        <Paragraph size="2">This guide explains how to use the reference.</Paragraph>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>

        <Paragraph size="2">
          You communicate to the Vimeo API through HTTP messages called <em>requests</em>.
          Especially if you're coming to us new, you might want to familiarize yourself with three
          essential components of an API request — endpoints, methods, and parameters — just so
          we're speaking the same language going forward. Your summary (or refresher course) awaits.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Headers are an equally important component of API requests. We
          touch on headers briefly here, but you can find more detailed information in{' '}
          <Link variant="minimal" href="/api/common-formats#working-with-headers">
            Using Common Formats and Parameters
          </Link>
          .
        </StyledNotice>

        <Header size="3" id="about-endpoints">
          About endpoints
        </Header>

        <Paragraph size="2">
          An API <em>endpoint</em> is a path that uniquely identifies a Vimeo resource: anything
          from a video to a user account. The exact path of the endpoint differs depending on the
          type of resource, but you always append it to <code>https://api.vimeo.com</code>.
        </Paragraph>

        <Paragraph size="2">
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          shows a few of the most common endpoints from around these parts. Keep in mind that these
          are just a few; you'll find the rest throughout this guide.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description="Common endpoints by resource type"
          headers={['Resource', 'Endpoint*']}
          rows={[
            ['Video', <code>https://api.vimeo.com/videos/{`{video_id}`}</code>],
            ['User', <code>https://api.vimeo.com/users/{`{user_id}`}</code>],
            [
              'Showcase (previously album)',
              <code>
                https://api.vimeo.com/users/{`{user_id}`}/albums/{`{showcase_id}`}
              </code>,
            ],
            ['Channel', <code>https://api.vimeo.com/channels/{`{channel_id}`}</code>],
            [
              'Folder',
              <code>
                https://api.vimeo.com/users/{`{user_id}`}/folders/{`{folder_id}`}
              </code>,
            ],
            ['Group', <code>https://api.vimeo.com/groups/{`{group_id}`}</code>],
          ]}
        />

        <Paragraph size="4">
          * Items between braces such as <code>{`{video_id}`}</code> represent parameter values.
        </Paragraph>

        <Paragraph size="2">
          We also provide a special endpoint, <code>/me</code>, which always refers to the currently
          authenticated user. It's the same as <code>/users/{`{user_id}`}</code>, in other words,
          but only when the ID in <code>{`{user_id}`}</code> is that of the authenticated user. You
          can use <code>/me</code> in conjunction with other user-specific resources like folders:{' '}
          <code>/me/folders/{`{folder_id}`}</code> instead of{' '}
          <code>
            /users/{`{user_id}`}/folders/{`{folder_id}`}
          </code>
          , for example.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> For more information about user authentication, see{' '}
          <Link variant="minimal" href="/api/authentication">
            Working with Authentication
          </Link>
          .
        </StyledNotice>

        <Header size="3" id="about-endpoints">
          About HTTP methods
        </Header>

        <Paragraph size="2">
          An <em>HTTP method</em> or <em>HTTP verb</em> describes how to interact with the resource
          that your endpoint identifies. The GET method, for example, retrieves the data associated
          with the endpoint, the POST method creates a new resource of that type, the DELETE method
          removes a specific resource of that type, and so on. See{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>{' '}
          for a complete rundown.
        </Paragraph>

        <GuideTable
          tableNumber={2}
          description="HTTP methods"
          headers={['Method', 'Description', 'Example']}
          rows={[
            [
              'GET',
              'Retrieves the JSON representation of a resource.',
              <span>
                <code>GET /me</code> returns the authenticated user's representation.
              </span>,
            ],
            [
              'POST',
              'Adds a new resource to a collection. This method generally requires additional parameters.',
              <span>
                <code>
                  POST /videos/{`{video_id}`}/tags/{`{word}`}
                </code>{' '}
                adds the tag{' '}
                <code>
                  <b>word</b>
                </code>{' '}
                to the video whose ID is{' '}
                <code>
                  <b>video_id</b>
                </code>
                .
              </span>,
            ],
            [
              'PUT',
              'Replaces a resource or associates one resource with another.',
              <span>
                <code>PUT /me/likes/{`{video_id}`}</code> adds a like on behalf of the authenticated
                user to the video whose ID is{' '}
                <code>
                  <b>video_id</b>
                </code>
                .
              </span>,
            ],
            [
              'PATCH',
              'Modifies a resource. The body of the request contains the new resource representation.',
              <span>
                <code>PATCH /videos/{`{video_id}`}</code> adds the tag{' '}
                <code>
                  <b>word</b>
                </code>{' '}
                to the video whose ID is{' '}
                <code>
                  <b>video_id</b>
                </code>
                .
              </span>,
            ],
            [
              'DELETE',
              'Deletes a resource or disassociates one resource from another.',
              <span>
                <code>DELETE /videos/{`{video_id}`}</code> deletes the video whose ID is{' '}
                <code>
                  <b>video_id</b>
                </code>
                .
              </span>,
            ],
          ]}
        />

        <Paragraph size="2">
          You don't need to memorize which method does what, just like you don't need to memorize
          the endpoint. For any given programming task, we indicate the proper method and endpoint
          in the reference.
        </Paragraph>

        <Header size="3" id="about-parameters">
          About parameters
        </Header>

        <Paragraph size="2">
          <em>Parameters</em> are variable values for distinguishing resources of a particular type
          or modifying the scope or behavior of the API request. Parameters can appear in the path
          of the endpoint, in a query string at the end of the endpoint, or in the body of the
          request. Exactly where you define the parameter value depends on the method and the
          parameter's function, among other factors (see{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>
          ).
        </Paragraph>

        <GuideTable
          tableNumber={3}
          description="Parameter types by HTTP method"
          headers={['Method', 'Parameter types']}
          rows={[
            ['GET', 'Path, query'],
            ['POST', 'Path, body'],
            ['PUT', 'Path, body'],
            ['PATCH', 'Path, body'],
            ['DELETE', 'Path, body'],
          ]}
        />

        <Paragraph size="2">
          Again, there's no need to memorize which parameter goes where. It's all spelled out in the
          reference.
        </Paragraph>

        <Header size="2" id="about-the-reference">
          About the reference
        </Header>

        <Paragraph size="2">
          We've organized the reference in an expandable hierarchy of groups and tasks, like{' '}
          <em>Upload a video</em> (under <strong>Videos</strong> {'>'} <strong>Uploads</strong>) or{' '}
          <em>Get a specific tag</em> (under <strong>Tags</strong> {'>'} <strong>Essentials</strong>
          ). Pick the task that you want to complete. We break down the details under a standard set
          of headings:
        </Paragraph>

        <ul>
          <li>
            <strong>Request.</strong> The request to make to the API.
          </li>
          <li>
            <strong>Path parameters.</strong> The path parameters to send (if any).
          </li>
          <li>
            <strong>Query parameters.</strong> The query parameters to send (if any).
          </li>
          <li>
            <strong>Body.</strong> The parameters or data to send in the body of the request (if
            needed).
          </li>
          <li>
            <strong>Response.</strong> The status code or data that the API returns in response to
            your request.
          </li>
        </ul>

        <Paragraph size="2">For specific information about each section, read on.</Paragraph>

        <Header size="3" id="request">
          Request
        </Header>

        <Paragraph size="2">
          This section of the reference shows the HTTP method and the full path of the endpoint or
          endpoints to use. If you need an access token with certain scopes to make an API request
          of this kind, we give you that information here.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> To learn more about access tokens and scopes, see{' '}
          <Link variant="minimal" href="/api/authentication">
            Working with Authentication
          </Link>
          .
        </StyledNotice>

        <Header size="3" id="path-parameters">
          Path parameters
        </Header>

        <Paragraph size="2">
          This section of the reference lists the parameters that you pass to the API in the path of
          the endpoint itself. These parameters appear in the path as placeholders like{' '}
          <code>{`{video_id}`}</code> or <code>{`{user_id}`}</code>. The braces are part of the
          placeholder name.
        </Paragraph>

        <Paragraph size="2">If there aren't any path parameters, we omit this section.</Paragraph>

        <Header size="3" id="query-parameters">
          Query parameters
        </Header>

        <Paragraph size="2">
          This section of the reference lists the parameters that you pass to the API in a query
          string at the end of the endpoint, like{' '}
          <code>https://api.vimeo.com/me/videos?sort=date&amp;direction=desc</code>.
        </Paragraph>

        <Paragraph size="2">If there aren't any query parameters, we omit this section.</Paragraph>

        <Header size="3" id="body">
          Body
        </Header>

        <Paragraph size="2">
          This section of the reference lists the parameters and other data that you pass to the API
          in the body of the request. For the{' '}
          <code>
            <b>Content-Type</b>
          </code>{' '}
          header, we recommend <code>application/json</code>, where you send us the data in JSON
          format, but you can also use <code>application/x-www-form-urlencoded</code> and send us
          form data instead.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> For nested parameters, send us nested JSON objects, or use dot
          notation if you're sending form data.
        </StyledNotice>

        <Paragraph size="2">If you don't need a body, we omit this section.</Paragraph>

        <Header size="3" id="response">
          Response
        </Header>

        <Paragraph size="2">
          This section of the reference provides information about the API response under three
          separate subheadings: Details, Example, and Reference.
        </Paragraph>

        <Header size="4" id="details">
          Details
        </Header>

        <Paragraph size="2">
          The Details section explains the meaning of HTTP status codes and error codes in the
          context of your request.
        </Paragraph>

        <Header size="4" id="example">
          Example
        </Header>

        <Paragraph size="2">
          The Example section includes a sample data representation of the kind that the request
          returns to you. If the request doesn't return data from the API, we omit this section.
        </Paragraph>

        <Header size="4" id="reference">
          Reference
        </Header>

        <Paragraph size="2">
          The Reference section links you to additional documentation for descriptions of every
          field in the representation. If the request doesn't return data from the API, we omit this
          section.
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>

        <Paragraph size="2">
          The Vimeo API is at your disposal. The time has come to ask: what can you do with it? For
          inspiration and examples tailor-made to newcomers, see our series of{' '}
          <Link variant="minimal" href="/api/guides/start">
            Getting Started
          </Link>{' '}
          guides.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
